import type { ShowcaseItem } from './featured-work';

import { upscaleImageHandlerUrl } from './upscale-image';

/**
 * Where the About band's lead media came from.
 *
 * `derived` is the lead item from {@link getAboutLead}: a manufacturer's first
 * featured product, otherwise the professional's lead featured project, or a
 * project owned by someone else that used their products.
 */
export type AboutFeatureKind = 'authored-image' | 'authored-video' | 'derived';

export interface AboutFeatureVideo {
    url: string;
    /** True for a hosted YouTube/Vimeo link, false for an uploaded MP4. */
    isEmbed: boolean;
}

export interface AboutFeature {
    kind: AboutFeatureKind;
    /** Still frame: the authored image, the video's poster, or the lead item's thumbnail. */
    src: string;
    alt: string;
    /** Present only when kind is 'authored-video'. */
    video?: AboutFeatureVideo;
    /** Empty string means render no caption line, never borrow one. */
    caption: string;
    subcaption: string;
    /** Empty string means the media is not a link. */
    link: string;
    /**
     * Authored media earns a slot on mobile; derived media stays desktop-only,
     * where the editorial band was designed.
     */
    showOnMobile: boolean;
}

/**
 * The subset of the professional overview this decision reads. Kept structural
 * so the rule can be exercised without standing up a whole loader payload.
 */
export interface AboutFeatureSource {
    FeaturedContentImage?: {
        Caption?: string | null;
        LibraryImageFile?: {
            FrontendDetailPage?: { Desktop?: string | null } | null;
        } | null;
    } | null;
    FeaturedContentVideo?: string | null;
    FeaturedContentVideoEmbedUrl?: string | null;
    FeaturedContentCaption?: string | null;
    FeaturedContentSubcaption?: string | null;
    FeaturedContentLink?: string | null;
}

const clean = (value: string | null | undefined): string =>
    (value ?? '').trim();

/**
 * The brand's own band image, if it resolves.
 *
 * This one value decides everything: the backend requires a poster before a
 * video can be set, so a resolving image is the single signal that a brand has
 * authored an entry at all.
 */
const authoredImageSrc = (overview: AboutFeatureSource): string =>
    clean(
        overview.FeaturedContentImage?.LibraryImageFile?.FrontendDetailPage
            ?.Desktop,
    );

/**
 * Whether the band is showing the brand's own media rather than a derived
 * project. The page reads this to spare itself the surrounding spacing when the
 * band is hidden on mobile.
 */
export const hasAuthoredFeature = (overview: AboutFeatureSource): boolean =>
    authoredImageSrc(overview) !== '';

/**
 * Decide what the About band shows, preferring the brand's own entry over the
 * derived project.
 *
 * Authored-ness is deliberately atomic, and hangs off the image alone: the
 * backend requires a poster before a video can be set, so a resolving image is
 * the single signal that a brand has authored something. Once it has, none of
 * the copy falls back either — an authored image with no caption renders
 * captionless rather than borrowing the project's title. Mixing the two is the
 * exact failure this feature exists to remove: a brand's page captioning
 * another professional's project with that professional's name.
 */
export const resolveAboutFeature = (
    overview: AboutFeatureSource,
    lead: ShowcaseItem | undefined,
): AboutFeature | null => {
    const authoredSrc = authoredImageSrc(overview);

    if (authoredSrc) {
        const caption = clean(overview.FeaturedContentCaption);
        const video = authoredVideo(overview);

        return {
            kind: video ? 'authored-video' : 'authored-image',
            // Already a detail-sized rendition — the upscale helper exists to
            // undo the small box baked into *tile* URLs and has nothing to fix
            // here.
            src: authoredSrc,
            alt: clean(overview.FeaturedContentImage?.Caption) || caption,
            video,
            caption,
            subcaption: clean(overview.FeaturedContentSubcaption),
            link: clean(overview.FeaturedContentLink),
            showOnMobile: true,
        };
    }

    const derivedSrc = clean(lead?.images?.[0]?.src);
    if (!lead || !derivedSrc) {
        return null;
    }

    return {
        kind: 'derived',
        src: upscaleImageHandlerUrl(derivedSrc) ?? derivedSrc,
        alt: clean(lead.images?.[0]?.alt) || lead.title,
        caption: lead.title,
        subcaption: clean(lead.professional?.name),
        link: lead.link,
        showOnMobile: false,
    };
};

/**
 * An uploaded MP4 and a pasted link are mutually exclusive server-side, so
 * checking the upload first is a tie-break that should never be needed.
 */
const authoredVideo = (
    overview: AboutFeatureSource,
): AboutFeatureVideo | undefined => {
    const file = clean(overview.FeaturedContentVideo);
    if (file) {
        return { url: file, isEmbed: false };
    }

    const embed = clean(overview.FeaturedContentVideoEmbedUrl);

    return embed ? { url: embed, isEmbed: true } : undefined;
};
